<script setup lang="ts">
import { computed } from 'vue';
import { useEditorStore } from '@/stores/editor';
import { useMapStore } from '@/stores/map';

const editorStore = useEditorStore();
const mapStore = useMapStore();

// Current tile under cursor
const currentTile = computed(() => {
    return mapStore.getTile(editorStore.tileX, editorStore.tileY);
});

// Toggle flags on current tile
function toggleBlocked(): void {
    if (!currentTile.value) return;
    mapStore.beginOperation();
    mapStore.setTileFlags(editorStore.tileX, editorStore.tileY, {
        blocked: !currentTile.value.blocked
    });
    mapStore.endOperation();
}

function toggleTeleport(): void {
    if (!currentTile.value) return;
    mapStore.beginOperation();
    mapStore.setTileFlags(editorStore.tileX, editorStore.tileY, {
        teleport: !currentTile.value.teleport
    });
    mapStore.endOperation();
}

function toggleFarming(): void {
    if (!currentTile.value) return;
    mapStore.beginOperation();
    mapStore.setTileFlags(editorStore.tileX, editorStore.tileY, {
        farming: !currentTile.value.farming
    });
    mapStore.endOperation();
}

function toggleBuildable(): void {
    if (!currentTile.value) return;
    mapStore.beginOperation();
    mapStore.setTileFlags(editorStore.tileX, editorStore.tileY, {
        buildable: !currentTile.value.buildable
    });
    mapStore.endOperation();
}

function clearObject(): void {
    mapStore.beginOperation();
    mapStore.clearObject(editorStore.tileX, editorStore.tileY);
    mapStore.endOperation();
}
</script>

<template>
    <div class="properties-panel">
        <div class="panel-header">
            <h3>Properties</h3>
        </div>

        <div class="panel-content" v-if="currentTile">
            <!-- Position -->
            <div class="property-section">
                <h4>Position</h4>
                <div class="property-row">
                    <span class="property-label">X:</span>
                    <span class="property-value">{{ editorStore.tileX }}</span>
                </div>
                <div class="property-row">
                    <span class="property-label">Y:</span>
                    <span class="property-value">{{ editorStore.tileY }}</span>
                </div>
            </div>

            <!-- Base Tile -->
            <div class="property-section">
                <h4>Base Tile</h4>
                <div class="property-row">
                    <span class="property-label">Sprite:</span>
                    <span class="property-value">{{ currentTile.tileSprite }}</span>
                </div>
                <div class="property-row">
                    <span class="property-label">Frame:</span>
                    <span class="property-value">{{ currentTile.tileSpriteFrame }}</span>
                </div>
            </div>

            <!-- Object -->
            <div class="property-section" v-if="currentTile.objectSprite > 0">
                <h4>Object</h4>
                <div class="property-row">
                    <span class="property-label">Sprite:</span>
                    <span class="property-value">{{ currentTile.objectSprite }}</span>
                </div>
                <div class="property-row">
                    <span class="property-label">Frame:</span>
                    <span class="property-value">{{ currentTile.objectSpriteFrame }}</span>
                </div>
                <button class="clear-btn" @click="clearObject">Clear Object</button>
            </div>

            <!-- Flags -->
            <div class="property-section">
                <h4>Flags</h4>
                <label class="flag-toggle">
                    <input
                        type="checkbox"
                        :checked="currentTile.blocked"
                        @change="toggleBlocked"
                    />
                    <span>Blocked</span>
                </label>
                <label class="flag-toggle">
                    <input
                        type="checkbox"
                        :checked="currentTile.teleport"
                        @change="toggleTeleport"
                    />
                    <span>Teleport</span>
                </label>
                <label class="flag-toggle">
                    <input
                        type="checkbox"
                        :checked="currentTile.farming"
                        @change="toggleFarming"
                    />
                    <span>Farming</span>
                </label>
                <label class="flag-toggle">
                    <input
                        type="checkbox"
                        :checked="currentTile.buildable"
                        @change="toggleBuildable"
                    />
                    <span>Buildable</span>
                </label>
            </div>
        </div>

        <div class="panel-content empty" v-else>
            <p>Hover over a tile to see its properties</p>
        </div>
    </div>
</template>

<style scoped>
.properties-panel {
    overflow: hidden;
}

.panel-header {
    padding: 8px 12px;
    background: rgba(0, 0, 0, 0.2);
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 8px 8px 0 0;
}

.panel-header h3 {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: var(--text-secondary);
}

.panel-content {
    padding: 12px;
}

.panel-content.empty {
    text-align: center;
    color: var(--text-secondary);
    font-size: 12px;
    padding: 24px 12px;
}

.property-section {
    margin-bottom: 16px;
}

.property-section:last-child {
    margin-bottom: 0;
}

.property-section h4 {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: var(--text-secondary);
    margin-bottom: 8px;
}

.property-row {
    display: flex;
    justify-content: space-between;
    font-size: 12px;
    padding: 2px 0;
}

.property-label {
    color: var(--text-secondary);
}

.property-value {
    color: var(--text-primary);
    font-family: monospace;
}

.flag-toggle {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 4px 0;
    font-size: 12px;
    cursor: pointer;
}

.flag-toggle input {
    width: 14px;
    height: 14px;
    cursor: pointer;
}

.flag-toggle span {
    color: var(--text-primary);
}

.clear-btn {
    width: 100%;
    margin-top: 8px;
    padding: 6px 12px;
    background: var(--bg-tertiary);
    border: 1px solid var(--border-color);
    border-radius: 4px;
    color: var(--text-secondary);
    font-size: 12px;
    cursor: pointer;
    transition: all 0.15s ease;
}

.clear-btn:hover {
    background: var(--error-color);
    border-color: var(--error-color);
    color: white;
}
</style>
